/* eslint-disable @typescript-eslint/no-explicit-any */
// Benefits Helper Functions for MembershipBenefits Component
// These functions handle the dynamic benefits logic based on integrated AEM structure

import React, { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { MemberStatusInfo } from '../../organisms/MembershipBenefits/MembershipBenefits.types';
import {
  MEMBER_STATUS,
  MEMBER_NIGHT_RANGES,
  OVERVIEW_CONSTANT,
  MEMBER_LEVEL_CODE,
  PLATINUM_CORE_CODE,
  MEMBERSHIP_LIFETIME_CODES_MAPPING,
  MEMBERSHIP_CODES_MAPPING,
  CORE_LEVEL_CODES,
  BENEFITS_DISPLAY,
  AmbassadorMemberCode,
  EARNINGS_SUMMARY_CONSTANTS,
} from './constants/constants';
import { replaceText } from './promotionHelper';
import { phoenixAccountGetMemberStatusDetails } from '../graph';
import { generateApolloClientHeaders, getMemberLevelPropertiesByCode, getMemberLevelV2 } from './helper';

// Type for integrated member level benefit structure
interface IntegratedMemberLevelBenefit {
  key: string;
  notNearTitle: string;
  notNearSubTitle: string;
  nearRenewingTitle?: string;
  nearRenewingSubTitle?: string;
  nearRenewingMinValue?: string;
  nearRenewingMaxValue?: string;
  nearAchievingTitle?: string;
  nearAchievingSubTitle?: string;
  nearAchievingMinValue?: string;
  nearAchievingMaxValue?: string;
  hundredPlus?: number | string;
  ninetyFiveToNinetyNine?: number;
}

/**
 * Determines member status based on current level, nights, spend, and integrated member level benefits
 * @param currentLevel - Current member level (R, S, G, P, PPC, PFS)
 * @param currentNights - Current year nights count
 * @param memberLevelBenefits - Array of integrated member level benefits
 * @param nextLevelMapping - Next level mapping from UXL
 * @param currentSpend - Current year spend (optional, for Ambassador logic)
 * @param lifetimeStatus - Lifetime status level (optional)
 * @returns MemberStatusInfo with status, benefits to show, and target information
 */

/**
 * Checks if a member has lifetime status matching their current tier level type
 * This is used for AC8 logic to determine if "Not Near" should be shown instead of "Near Renewing"
 * @param lifetimeStatus - The member's lifetime status level code
 * @param levelTypeCode - The member's current level type code
 * @returns boolean indicating if lifetime status matches current tier
 */
export const isLifetimeStatusMatchingCurrentTier = (
  lifetimeStatus: string | null | undefined,
  levelTypeCode: string | undefined
): boolean => {
  return !!(lifetimeStatus && levelTypeCode && lifetimeStatus === levelTypeCode);
};

/**
 * Checks if a member is in the "Near Renewing" status based on their tier and nights
 * @param levelCode - The member's current tier level code
 * @param currentNights - Number of nights stayed
 * @returns boolean indicating if member is near renewing
 */
export const checkNearRenewingStatus = (levelCode: string, currentNights: number): boolean => {
  const nearRenewingRanges = {
    S: MEMBER_NIGHT_RANGES.SILVER_NEAR_RENEWING, // 7-9 nights
    G: MEMBER_NIGHT_RANGES.GOLD_NEAR_RENEWING, // 18-24 nights
    P: MEMBER_NIGHT_RANGES.PLATINUM_NEAR_RENEWING, // 40-49 nights
    PPC: MEMBER_NIGHT_RANGES.TITANIUM_NEAR_RENEWING, // 65-74 nights
  };

  const range = nearRenewingRanges[levelCode as keyof typeof nearRenewingRanges];

  if (!range) {
    return false;
  }

  return currentNights >= range.min && currentNights <= range.max;
};

/**
 * Checks if a member is in the "Near Achieving" status based on their tier and nights
 * @param levelCode - The member's current tier level code
 * @param currentNights - Number of nights stayed
 * @returns boolean indicating if member is near achieving
 */
export const checkNearAchievingStatus = (levelCode: string, currentNights: number): boolean => {
  const nearAchievingRanges = {
    S: MEMBER_NIGHT_RANGES.SILVER_NEAR_ACHIEVING, // 18-24 nights to Gold
    G: MEMBER_NIGHT_RANGES.GOLD_NEAR_ACHIEVING, // 40-49 nights to Platinum
    P: MEMBER_NIGHT_RANGES.PLATINUM_NEAR_ACHIEVING, // 65-74 nights to Titanium
    PPE: MEMBER_NIGHT_RANGES.PLATINUM_NEAR_ACHIEVING, // 65-74 nights to Titanium (Platinum Elite)
    PPC: MEMBER_NIGHT_RANGES.TITANIUM_NEAR_ACHIEVING, // 95-99 nights to Ambassador
  };

  const range = nearAchievingRanges[levelCode as keyof typeof nearAchievingRanges];

  if (!range) {
    return false;
  }

  return currentNights >= range.min && currentNights <= range.max;
};

/**
 * Checks if a member is in the "Not Near" status based on their tier and nights
 * @param levelCode - The member's current tier level code
 * @param currentNights - Number of nights stayed
 * @returns boolean indicating if member is not near achieving or renewing
 */
export const checkNotNearStatus = (levelCode: string, currentNights: number): boolean => {
  const notNearRanges = {
    S: [
      MEMBER_NIGHT_RANGES.SILVER_NOT_NEAR, // 0-6 nights
      MEMBER_NIGHT_RANGES.SILVER_NOT_NEAR_2, // 10-17 nights
    ],
    G: [MEMBER_NIGHT_RANGES.GOLD_NOT_NEAR], // 0-17 nights
    P: [MEMBER_NIGHT_RANGES.PLATINUM_NOT_NEAR], // 0-39 nights
    PPC: [MEMBER_NIGHT_RANGES.TITANIUM_NOT_NEAR], // 0-64 nights
  };

  const ranges = notNearRanges[levelCode as keyof typeof notNearRanges];

  if (!ranges) {
    return false;
  }

  return ranges.some(range => currentNights >= range.min && currentNights <= range.max);
};

export const determineMemberStatus = (
  currentLevel: string,
  currentNights: number,
  memberLevelBenefits: IntegratedMemberLevelBenefit[],
  nextLevelMapping: Record<string, string>,
  currentSpend?: number,
  lifetimeStatus?: string | null,
  uxlData?: any
): MemberStatusInfo => {
  const levelCode = currentLevel.toUpperCase();

  // Handle lifetime status logic (AC5)
  // Compare lifetime status with level type code from UXL data, not the processed level code
  const levelTypeCode = uxlData?.customer?.loyaltyInformation?.rewards?.levelType?.code;
  if (isLifetimeStatusMatchingCurrentTier(lifetimeStatus, levelTypeCode)) {
    return {
      status: MEMBER_STATUS.NOT_NEAR,
      benefitsToShow: levelCode,
      isLocked: false,
      nightsToTarget: 0,
      targetTier: levelCode,
    };
  }

  // Find the current member level benefit
  const currentMemberBenefit = memberLevelBenefits.find(benefit => benefit.key === levelCode);

  if (!currentMemberBenefit) {
    return {
      status: MEMBER_STATUS.NOT_NEAR,
      benefitsToShow: levelCode,
      isLocked: false,
      nightsToTarget: 0,
      targetTier: levelCode,
    };
  }

  // Check UXL data first (takes precedence over AEM ranges)
  if (uxlData?.customer?.loyaltyInformation?.rewards) {
    const uxlRewards = uxlData.customer.loyaltyInformation.rewards;
    const eliteNightsToRenewCurrent = uxlRewards.eliteNightsToRenewCurrent;
    const eliteNightsToAchieveNext = uxlRewards.eliteNightsToAchieveNext;

    // WEB-180632: Handle case where user has ALREADY MET requirements for next tier
    // When eliteNightsToAchieveNext === 0, it means the user has met the night requirements
    // This applies to all tiers, not just Titanium
    if (eliteNightsToAchieveNext === 0 && nextLevelMapping[levelCode]) {
      const nextLevel = nextLevelMapping[levelCode];

      // Check if user has met the night requirements for the next tier
      const tierNightRequirements: Record<string, number> = {
        R: 0, // Member (Basic)
        S: 10, // Silver Elite requires 10 nights
        G: 25, // Gold Elite requires 25 nights
        P: 50, // Platinum Elite requires 50 nights
        PPC: 75, // Titanium Elite requires 75 nights
        PFS: 100, // Ambassador Elite requires 100 nights
      };

      const nextTierNightsRequired = tierNightRequirements[nextLevel] || 0;
      const hasMetNightRequirement = currentNights >= nextTierNightsRequired;

      // Special case for Titanium → Ambassador: also requires spend threshold
      if (levelCode === OVERVIEW_CONSTANT.TITANIUM_CODE[2]) {
        const hasMetSpendRequirement =
          currentSpend &&
          currentMemberBenefit.hundredPlus &&
          currentSpend >= parseInt(String(currentMemberBenefit.hundredPlus));

        if (hasMetNightRequirement && hasMetSpendRequirement) {
          return {
            status: MEMBER_STATUS.NEAR_ACHIEVING,
            benefitsToShow: nextLevel,
            isLocked: true,
            nightsToTarget: 0,
            targetTier: nextLevel,
          };
        }
      } else if (hasMetNightRequirement && levelCode !== OVERVIEW_CONSTANT.AMBASSADOR_CODE[0]) {
        // For all other tiers (except Ambassador which has no next tier),
        // if nights requirement is met, show next tier
        return {
          status: MEMBER_STATUS.NEAR_ACHIEVING,
          benefitsToShow: nextLevel,
          isLocked: true,
          nightsToTarget: 0,
          targetTier: nextLevel,
        };
      }
    }

    // Check for "near achieving" status FIRST (takes priority over near renewing)
    // User progressing to next tier is more important than renewing current tier
    if (eliteNightsToAchieveNext && eliteNightsToAchieveNext > 0) {
      const nextLevel = nextLevelMapping[levelCode];

      // Special case for Basic Member (R) - check night range (7-9 for near achieving)
      if (levelCode === MEMBER_LEVEL_CODE) {
        // Basic Member is only "Near Achieving" if they have 7-9 nights
        if (
          currentNights >= MEMBER_NIGHT_RANGES.BASIC_NEAR_ACHIEVING.min &&
          currentNights <= MEMBER_NIGHT_RANGES.BASIC_NEAR_ACHIEVING.max
        ) {
          return {
            status: MEMBER_STATUS.NEAR_ACHIEVING,
            benefitsToShow: nextLevel, // Show next tier benefits (Silver)
            isLocked: true, // Lock the next tier benefits
            nightsToTarget: eliteNightsToAchieveNext,
            targetTier: nextLevel,
          };
        }
        // If not in near achieving range, fall through to check near renewing
      } else {
        // For all other tiers, check night ranges for "Near Achieving" status
        const isNearAchieving = checkNearAchievingStatus(levelCode, currentNights);

        // Special case for Titanium with 100+ nights - also consider Near Achieving
        const isTitaniumWith100PlusNights = levelCode === OVERVIEW_CONSTANT.TITANIUM_CODE[2] && currentNights >= 100;

        // For Titanium → Ambassador, also need to check spend requirement ($22K)
        // This applies to both 95-99 nights AND 100+ nights scenarios
        const isTitaniumMember = levelCode === OVERVIEW_CONSTANT.TITANIUM_CODE[2];
        if (isTitaniumMember && (isNearAchieving || isTitaniumWith100PlusNights)) {
          // Titanium requires spend check: $22K for 95-99 nights or 100+ nights
          const spendThreshold = currentMemberBenefit?.hundredPlus
            ? parseInt(String(currentMemberBenefit.hundredPlus))
            : EARNINGS_SUMMARY_CONSTANTS.TITANIUM_SPEND_THRESHOLD_LOW; // Default to $22K if not configured
          const hasMetSpendRequirement = currentSpend && currentSpend >= spendThreshold;

          if (hasMetSpendRequirement) {
            const result = {
              status: MEMBER_STATUS.NEAR_ACHIEVING,
              benefitsToShow: nextLevel, // Show next tier benefits
              isLocked: true, // Lock the next tier benefits
              nightsToTarget: eliteNightsToAchieveNext,
              targetTier: nextLevel,
            };
            return result;
          }
          // If spend requirement not met, fall through to check near renewing or not-near
        } else if (isNearAchieving || isTitaniumWith100PlusNights) {
          // Non-Titanium tiers don't require spend check
          const result = {
            status: MEMBER_STATUS.NEAR_ACHIEVING,
            benefitsToShow: nextLevel, // Show next tier benefits
            isLocked: true, // Lock the next tier benefits
            nightsToTarget: eliteNightsToAchieveNext,
            targetTier: nextLevel,
          };
          return result;
        }
        // If not in near achieving range, fall through to check near renewing
      }
    }

    // Check for "near renewing" status using UXL data (only if not near achieving)
    if (eliteNightsToRenewCurrent && eliteNightsToRenewCurrent > 0) {
      // AC8: If Lifetime Status and current tier matches lifetime level, show "Not Near" instead of "Near Renewing"
      if (isLifetimeStatusMatchingCurrentTier(lifetimeStatus, levelTypeCode)) {
        return {
          status: MEMBER_STATUS.NOT_NEAR,
          benefitsToShow: levelCode,
          isLocked: false,
          nightsToTarget: 0,
          targetTier: levelCode,
        };
      }

      const isNearRenewing = checkNearRenewingStatus(levelCode, currentNights);

      if (isNearRenewing) {
        return {
          status: MEMBER_STATUS.NEAR_RENEWING,
          benefitsToShow: levelCode,
          isLocked: false,
          nightsToTarget: eliteNightsToRenewCurrent,
          targetTier: levelCode,
        };
      }
    }

    // Check for "Not Near" status - explicit validation for ranges outside Near Renewing/Achieving
    const isNotNear = checkNotNearStatus(levelCode, currentNights);
    if (isNotNear) {
      return {
        status: MEMBER_STATUS.NOT_NEAR,
        benefitsToShow: levelCode,
        isLocked: false,
        nightsToTarget: 0,
        targetTier: levelCode,
      };
    }

    // Default: Not Near (if no specific status matched)
    return {
      status: MEMBER_STATUS.NOT_NEAR,
      benefitsToShow: levelCode,
      isLocked: false,
      nightsToTarget: 0,
      targetTier: levelCode,
    };
  }

  // Special case for Titanium near achieving Ambassador (handle 100+ nights first)
  if (
    levelCode === OVERVIEW_CONSTANT.TITANIUM_CODE[2] &&
    currentNights >= 100 &&
    currentSpend &&
    currentMemberBenefit.hundredPlus
  ) {
    if (currentSpend >= parseInt(String(currentMemberBenefit.hundredPlus))) {
      return {
        status: MEMBER_STATUS.NEAR_ACHIEVING,
        benefitsToShow: OVERVIEW_CONSTANT.AMBASSADOR_CODE[0],
        isLocked: true, // Show next tier benefits as LOCKED (per requirements)
        nightsToTarget: 0,
        targetTier: OVERVIEW_CONSTANT.AMBASSADOR_CODE[0],
      };
    }
  }

  // Check for "near achieving" status
  if (
    currentMemberBenefit.nearAchievingMinValue &&
    currentMemberBenefit.nearAchievingMaxValue &&
    currentNights >= parseInt(currentMemberBenefit.nearAchievingMinValue) &&
    currentNights <= parseInt(currentMemberBenefit.nearAchievingMaxValue)
  ) {
    // Special case for Basic Member (R) - never lock their benefits
    if (levelCode === MEMBER_LEVEL_CODE) {
      const nextLevel = nextLevelMapping[levelCode];
      return {
        status: MEMBER_STATUS.NEAR_ACHIEVING,
        benefitsToShow: levelCode, // Show current tier benefits (Basic Member)
        isLocked: false, // Basic Member benefits are never locked
        nightsToTarget: parseInt(currentMemberBenefit.nearAchievingMaxValue) + 1 - currentNights,
        targetTier: nextLevel, // Next tier for messaging purposes
      };
    }

    // Special case for Titanium near achieving Ambassador (95-99 nights)
    // For Titanium in near achieving range, check spend requirement ($22K)
    if (
      levelCode === OVERVIEW_CONSTANT.TITANIUM_CODE[2] &&
      currentNights >= MEMBER_NIGHT_RANGES.TITANIUM_NEAR_ACHIEVING.min &&
      currentNights <= MEMBER_NIGHT_RANGES.TITANIUM_NEAR_ACHIEVING.max
    ) {
      // Titanium → Ambassador requires $22K spend
      const spendThreshold = currentMemberBenefit.hundredPlus
        ? parseInt(String(currentMemberBenefit.hundredPlus))
        : EARNINGS_SUMMARY_CONSTANTS.TITANIUM_SPEND_THRESHOLD_LOW; // Default to $22K if not configured
      const hasMetSpendRequirement = currentSpend && currentSpend >= spendThreshold;

      if (hasMetSpendRequirement) {
        return {
          status: MEMBER_STATUS.NEAR_ACHIEVING,
          benefitsToShow: OVERVIEW_CONSTANT.AMBASSADOR_CODE[0],
          isLocked: true, // Show next tier benefits as LOCKED (per requirements)
          nightsToTarget: parseInt(currentMemberBenefit.nearAchievingMaxValue) + 1 - currentNights,
          targetTier: OVERVIEW_CONSTANT.AMBASSADOR_CODE[0],
        };
      }
      // If spend requirement not met, Titanium should NOT show as near achieving
      // Fall through to near renewing or not-near check (skip regular near achieving logic)
    } else if (levelCode !== OVERVIEW_CONSTANT.TITANIUM_CODE[2]) {
      // Regular near achieving logic for other tiers (NOT Titanium)
      const nextLevel = nextLevelMapping[levelCode];
      const result = {
        status: MEMBER_STATUS.NEAR_ACHIEVING,
        benefitsToShow: nextLevel, // Show next tier benefits (locked) - per requirements
        isLocked: true, // Next tier benefits are locked - per requirements
        nightsToTarget: parseInt(currentMemberBenefit.nearAchievingMaxValue) + 1 - currentNights,
        targetTier: nextLevel, // Next tier for messaging purposes
      };

      return result;
    }
  }

  // Check for "near renewing" status
  if (
    currentMemberBenefit.nearRenewingMinValue &&
    currentMemberBenefit.nearRenewingMaxValue &&
    currentNights >= parseInt(currentMemberBenefit.nearRenewingMinValue) &&
    currentNights <= parseInt(currentMemberBenefit.nearRenewingMaxValue)
  ) {
    // AC8: If Lifetime Status and current tier matches lifetime level, show "Not Near" instead of "Near Renewing"
    if (isLifetimeStatusMatchingCurrentTier(lifetimeStatus, levelTypeCode)) {
      return {
        status: MEMBER_STATUS.NOT_NEAR,
        benefitsToShow: levelCode,
        isLocked: false,
        nightsToTarget: 0,
        targetTier: levelCode,
      };
    }

    return {
      status: MEMBER_STATUS.NEAR_RENEWING,
      benefitsToShow: levelCode,
      isLocked: false,
      nightsToTarget: parseInt(currentMemberBenefit.nearRenewingMaxValue) + 1 - currentNights,
      targetTier: levelCode,
    };
  }

  // Default: "not near" status
  return {
    status: MEMBER_STATUS.NOT_NEAR,
    benefitsToShow: levelCode,
    isLocked: false,
    nightsToTarget: 0,
    targetTier: levelCode,
  };
};

/**
 * Generates dynamic content (title and subtitle) based on member status and integrated member level benefit
 * @param memberStatus - Member status information
 * @param memberLevel - Current member level
 * @param memberLevelBenefit - Integrated member level benefit containing dynamic content
 * @returns Object with title and subtitle
 */
export const generateDynamicContent = (
  memberStatus: MemberStatusInfo,
  _memberLevel: string,
  memberLevelBenefit: IntegratedMemberLevelBenefit,
  nightsLabel: string,
  nightLabel: string
): { title: string; subtitle: string } | null => {
  const status = memberStatus.status;

  // Ensure we have valid member level benefit data
  if (!memberLevelBenefit) {
    // Return null to indicate missing data - let the calling code handle the fallback
    return null;
  }

  let template: { title: string; subtitle: string } | undefined;

  switch (status) {
    case MEMBER_STATUS.NEAR_ACHIEVING:
      template = {
        title: memberLevelBenefit.nearAchievingTitle || memberLevelBenefit.notNearTitle,
        subtitle: memberLevelBenefit.nearAchievingSubTitle || memberLevelBenefit.notNearSubTitle,
      };
      break;
    case MEMBER_STATUS.NEAR_RENEWING:
      template = {
        title: memberLevelBenefit.nearRenewingTitle || memberLevelBenefit.notNearTitle,
        subtitle: memberLevelBenefit.nearRenewingSubTitle || memberLevelBenefit.notNearSubTitle,
      };
      break;
    case MEMBER_STATUS.NOT_NEAR:
    default:
      template = {
        title: memberLevelBenefit.notNearTitle,
        subtitle: memberLevelBenefit.notNearSubTitle,
      };
      break;
  }

  // Check if template exists and has required properties
  if (!template || !template.title) {
    // Return null to indicate missing template data - let the calling code handle the fallback
    return null;
  }

  // Replace placeholders in templates using AEM standard format {0}, {1}, etc.
  const title = replaceText(template.title, [
    memberStatus.nightsToTarget.toString(),
    memberStatus?.nightsToTarget === 1 ? nightLabel : nightsLabel,
  ]);

  return {
    title,
    subtitle: template.subtitle,
  };
};

/**
 * Sorts tiers according to the proper hierarchy order
 * @param tierList - Array of tier objects with id property
 * @returns Sorted tier list with Ambassador at top, Member at bottom
 */
export const sortTiersByHierarchy = <T extends { id: string }>(tierList: T[]): T[] => {
  if (!tierList || tierList.length === 0) {
    return tierList;
  }

  // Sort tiers according to CORE_LEVEL_CODES hierarchy (R, S, G, P, PPC, PFS)
  // This ensures proper order: Ambassador (top) -> Titanium -> Platinum -> Gold -> Silver -> Member (bottom)
  const sorted = [...tierList].sort((a, b) => {
    const indexA = CORE_LEVEL_CODES.indexOf(a.id);
    const indexB = CORE_LEVEL_CODES.indexOf(b.id);

    // If both tiers are in CORE_LEVEL_CODES, sort by their position (descending for top-to-bottom display)
    if (indexA !== -1 && indexB !== -1) {
      return indexB - indexA; // Descending: higher index (higher tier) comes first
    }

    // If only one is in CORE_LEVEL_CODES, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in CORE_LEVEL_CODES, maintain original order
    return 0;
  });

  return sorted;
};

/**
 * Creates member data structure from dataLayer for fallback when UXL data is not available
 * @param dataLayer - DataLayer object containing member information
 * @returns Formatted member data structure matching UXL format
 */
export const createMemberDataFromDataLayer = (dataLayer: Record<string, unknown>) => {
  if (!dataLayer) return null;

  return {
    customer: {
      profile: {
        name: {
          givenName: 'Member',
        },
      },
      loyaltyInformation: {
        rewards: {
          level: {
            code: (dataLayer['mr_prof_rewards_level'] as string)?.charAt(0) || MEMBER_LEVEL_CODE,
          },
          levelType: {
            code: (dataLayer['mr_prof_member_level_abbr'] as string) || '',
          },
          currentPointBalance: (dataLayer['mr_prof_points_balance'] as number) || 0,
          number: parseInt((dataLayer['mr_prof_member_program_level'] as string)?.split(':')[1] || '0', 10),
          datePointsExpire: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          enrollmentDate: new Date().toISOString(),
          eliteLifetimeNights: (dataLayer['mr_prof_nights_lifetime'] as number) || 0,
          eliteNightsToAchieveNext: (dataLayer['mr_prof_nights_to_next_level'] as number) || 0,
          eliteNightsToRenewCurrent: (dataLayer['mr_prof_nights_to_renew_current'] as number) || 0,
          eliteLifetimeLevel: {
            code: (dataLayer['mr_prof_member_level_abbr'] as string) || '',
          },
          nextLevelType: {
            code: '',
          },
          nextLevel: {
            code: '',
          },
        },
        rewardsSummary: {
          yearly: [
            {
              totalNights: (dataLayer['mr_prof_nights_booked_this_year'] as number) || 0,
              year: new Date().getFullYear(),
              totalRevenue: 0,
            },
          ],
        },
      },
    },
  };
};

/**
 * Maps member level codes using the membership codes mapping
 * @param code - The level code to map
 * @returns Mapped level code or original code if no mapping found
 */
export const getMemberLevelCode = (code: string) => {
  for (const [key, value] of MEMBERSHIP_CODES_MAPPING) {
    if (key?.includes(code)) {
      return value;
    }
  }
  return code;
};

/**
 * Gets next level mapping from props or UXL data
 * @param nextLevelMapping - Optional next level mapping from props
 * @param isAuthorMode - Whether in author mode
 * @param memberData - UXL member data
 * @param uxlMockData - Mock data for author mode
 * @returns Next level mapping object
 */
export const getNextLevelMapping = (
  nextLevelMapping?: Record<string, string>,
  isAuthorMode?: boolean,
  memberData?: Record<string, unknown>,
  uxlMockData?: Record<string, unknown>
) => {
  if (nextLevelMapping) {
    return nextLevelMapping;
  }

  const uxlData = isAuthorMode ? uxlMockData : memberData;
  const mapping: Record<string, string> = {};

  const customer = uxlData?.['customer'] as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (customer?.loyaltyInformation?.rewards?.nextLevel?.code) {
    const loyaltyInfo = customer.loyaltyInformation as any; // eslint-disable-line @typescript-eslint/no-explicit-any
    const rewards = loyaltyInfo.rewards as any; // eslint-disable-line @typescript-eslint/no-explicit-any
    const levelCode = rewards.level.code;
    const levelTypeCode = rewards.levelType.code;
    const nextLevel = rewards.nextLevel.code;
    const nextLevelType = rewards.nextLevelType?.code;

    // Use the same logic as getMemberLevel to get the correct current level code
    const currentLevel = getMemberLevelV2(levelCode, levelTypeCode);
    if (currentLevel) {
      // Hybrid approach: Use different logic based on tier level
      // - Up to Gold (R, S, G): Use nextLevel.code (basic codes)
      // - Platinum and above: Use nextLevelType.code (complex codes)
      const isPlatinumOrAbove = [
        PLATINUM_CORE_CODE, // Basic Platinum code
        ...OVERVIEW_CONSTANT.PLATINUM_CODE,
        ...OVERVIEW_CONSTANT.TITANIUM_CODE,
        ...OVERVIEW_CONSTANT.AMBASSADOR_CODE,
      ].includes(currentLevel);
      const nextTierCode = isPlatinumOrAbove ? nextLevelType || nextLevel : nextLevel;
      mapping[currentLevel] = nextTierCode;
    }
  }

  return mapping;
};

/**
 * Hook-like function to handle tier positioning logic
 *
 * REUSABILITY: This function can be used in any component that needs:
 * - Member tier positioning and centering
 * - Tier list reordering based on current user
 * - Active tier state management
 *
 * USAGE: Import and call within useEffect hooks in other components
 * that need similar tier positioning functionality.
 *
 * Can be reused across different components
 */
export const handleTierPositioningLogic = (
  memberData: any,
  memberStatusList: any[],
  memberLevels: any[],
  isAuthorMode: boolean,
  hasInitialized: React.MutableRefObject<boolean>,
  isUserInteracting: React.MutableRefObject<boolean>,
  setActiveIndex: (index: number) => void,
  setCurrentMember: (member: string) => void,
  getMemberLevelCode: (code: string) => string,
  memberLevelBenefits?: any[],
  nextLevelMapping?: Record<string, string>
) => {
  if (
    memberData &&
    memberStatusList &&
    memberLevels &&
    memberLevels.length > 0 &&
    !isAuthorMode &&
    !hasInitialized.current &&
    !isUserInteracting.current
  ) {
    const levelCode = memberData?.customer?.loyaltyInformation?.rewards?.level?.code;
    const levelTypeCode = memberData?.customer?.loyaltyInformation?.rewards?.levelType?.code;

    // Ensure both level codes exist before proceeding
    if (!levelCode || !levelTypeCode) {
      return;
    }

    const code = getMemberLevelV2(levelCode, levelTypeCode);

    if (code) {
      const mappedCode = MEMBERSHIP_LIFETIME_CODES_MAPPING[code] || getMemberLevelCode(code) || code;

      // Use existing tier order to prevent double-reversing
      const reorderedList = memberLevels;
      let targetIndex = reorderedList?.findIndex((item: any) => item?.id === mappedCode);
      let targetMember = mappedCode;

      if (memberLevelBenefits && nextLevelMapping) {
        const currentNights = Number(
          memberData?.customer?.loyaltyInformation?.rewardsSummary?.yearly?.[0]?.totalNights || 0
        );
        const currentSpend = memberData?.customer?.loyaltyInformation?.rewardsSummary?.yearly?.[0]?.totalRevenue;
        const lifetimeStatus = memberData?.customer?.loyaltyInformation?.rewards?.eliteLifetimeLevel?.code;

        const memberStatus = determineMemberStatus(
          code,
          currentNights,
          memberLevelBenefits,
          nextLevelMapping,
          currentSpend,
          lifetimeStatus,
          memberData
        );

        // Adjust target tier based on member status
        if (memberStatus.status === MEMBER_STATUS.NEAR_ACHIEVING && memberStatus.targetTier) {
          const nextTierIndex = reorderedList?.findIndex((item: any) => item?.id === memberStatus.targetTier);
          if (nextTierIndex !== -1) {
            targetIndex = nextTierIndex;
            targetMember = memberStatus.targetTier;
          }
        } else if (memberStatus.status === MEMBER_STATUS.NEAR_RENEWING) {
          targetIndex = reorderedList?.findIndex((item: any) => item?.id === mappedCode);
          targetMember = mappedCode;
        }
      }

      if (targetIndex !== -1) {
        setActiveIndex(targetIndex);
        setCurrentMember(targetMember);
        hasInitialized.current = true;
      }
    }
  }
};

/**
 * Hook-like function to handle Swiper scrolling logic
 *
 * REUSABILITY: This function can be used in any component that needs:
 * - Swiper carousel programmatic scrolling
 * - Smooth slide transitions with animation
 * - Active slide positioning
 *
 * USAGE: Import and call within useEffect hooks in other components
 * that use Swiper and need programmatic slide control.
 *
 * Can be reused across different components
 */
export const handleSwiperScrollingLogic = (
  swiperRef: React.MutableRefObject<any>,
  activeIndex: number | undefined,
  memberLevels: any[]
) => {
  if (swiperRef.current && activeIndex !== undefined && memberLevels.length > 0) {
    // Use setTimeout to ensure the Swiper is fully rendered before scrolling
    setTimeout(() => {
      if (swiperRef.current && swiperRef.current.slideTo) {
        swiperRef.current.slideTo(activeIndex, 1000); // 1000ms animation duration
      }
    }, 100);
  }
};

/**
 * Hook-like function to handle UXL data processing
 *
 * REUSABILITY: This function can be used in any component that needs:
 * - UXL data validation and processing
 * - Conditional data loading based on availability
 * - State management for data processing flags
 *
 * USAGE: Import and call within useEffect hooks in other components
 * that need similar UXL data processing functionality.
 *
 * Can be reused across different components
 */
export const handleUXLDataProcessingLogic = (
  memberData: any,
  memberStatusList: any[],
  memberLevelBenefits: any[],
  isAuthorMode: boolean,
  hasProcessedUXLData: React.MutableRefObject<boolean>,
  isUserInteracting: React.MutableRefObject<boolean>,
  processUXLData: () => void
) => {
  // Only process UXL data when all required data is available and not in author mode
  if (
    memberData &&
    memberStatusList &&
    memberLevelBenefits &&
    !isAuthorMode &&
    !hasProcessedUXLData.current &&
    !isUserInteracting.current
  ) {
    // All required data is available, process UXL data
    processUXLData();
    hasProcessedUXLData.current = true;
  }
};

/**
 * Processes member data and sets tier selection based on member status
 * Handles both author mode and production mode data processing
 *
 * REUSABILITY: This function can be used in any component that needs:
 * - Member data processing with tier selection
 * - Dynamic content generation
 * - Benefits state management
 *
 * USAGE: Import and use in components that need member data processing:
 * - MembershipBenefits (author mode and UXL processing)
 * - Other components that need similar functionality
 *
 * Can be reused across different components
 */
export const processMemberDataWithTierSelection = (
  dataSource: any,
  memberStatusList: any[],
  memberLevels: any[],
  memberLevelBenefits: any[],
  nextLevelMapping: Record<string, string>,
  _isAuthorMode: boolean,
  setCurrentMember: (member: string) => void,
  setDynamicBenefitsInfo: (info: any) => void,
  setModalMemberBenefits: (benefits: any) => void,
  setMemberProperties: (properties: any) => void,
  setActiveIndex: (index: number) => void,
  hasInitialized: React.MutableRefObject<boolean>,
  setUserActualTier?: (tier: string) => void,
  nightsLabel?: string,
  nightLabel?: string
) => {
  const mockData = dataSource?.customer?.loyaltyInformation?.rewards;
  const levelCode = mockData?.level?.code;
  const levelTypeCode = mockData?.levelType?.code;

  // Ensure we have both level codes before proceeding (prevents first load showing wrong tier)
  if (!levelCode || !levelTypeCode) {
    return;
  }

  const code = getMemberLevelV2(levelCode, levelTypeCode);
  if (!code) return;

  const mappedCode = MEMBERSHIP_LIFETIME_CODES_MAPPING[code] || getMemberLevelCode(code) || code;
  const member = memberStatusList?.find((item: any) => item?.id === mappedCode);
  setCurrentMember(member?.id);

  // Always preserve the user's actual tier for tick mark display
  if (setUserActualTier) {
    setUserActualTier(mappedCode);
  }

  const currentNights = Number(dataSource?.customer?.loyaltyInformation?.rewardsSummary?.yearly?.[0]?.totalNights || 0);
  const lifetimeStatus = mockData?.eliteLifetimeLevel?.code ? String(mockData.eliteLifetimeLevel.code) : null;
  const currentSpend = dataSource?.customer?.loyaltyInformation?.rewardsSummary?.yearly?.[0]?.totalRevenue;

  if (memberLevelBenefits && memberLevelBenefits.length > 0) {
    const memberStatus = determineMemberStatus(
      code,
      currentNights,
      memberLevelBenefits,
      nextLevelMapping,
      currentSpend,
      lifetimeStatus,
      dataSource
    );

    const currentMemberBenefit = memberLevelBenefits.find(benefit => benefit.key === code);
    let dynamicContent;

    if (currentMemberBenefit) {
      const benefitDataForMessaging = currentMemberBenefit;
      const generatedContent = generateDynamicContent(
        memberStatus,
        code,
        benefitDataForMessaging,
        nightsLabel || '',
        nightLabel || ''
      );
      if (generatedContent) {
        dynamicContent = generatedContent;
      } else {
        dynamicContent = {
          title: currentMemberBenefit.notNearTitle,
          subtitle: currentMemberBenefit.notNearSubTitle,
        };
      }
    } else {
      // Fallback: Find Member tier benefit specifically, not just first in array
      // This prevents using wrong tier data if array is sorted differently
      const fallbackBenefit = memberLevelBenefits.find(b => b.key === MEMBER_LEVEL_CODE);
      if (fallbackBenefit) {
        dynamicContent = {
          title: fallbackBenefit.notNearTitle,
          subtitle: fallbackBenefit.notNearSubTitle,
        };
      } else {
        // Last resort: use first available benefit
        const firstBenefit = memberLevelBenefits[0];
        dynamicContent = {
          title: firstBenefit?.notNearTitle || '',
          subtitle: firstBenefit?.notNearSubTitle || '',
        };
      }
    }

    const dynamicInfo = {
      memberStatus: memberStatus.status,
      benefitsToShow: memberStatus.benefitsToShow,
      isLocked: memberStatus.isLocked,
      nightsToTarget: memberStatus.nightsToTarget,
      targetTier: memberStatus.targetTier,
      dynamicTitle: dynamicContent?.title || '',
      dynamicSubtitle: dynamicContent?.subtitle || '',
    };

    setDynamicBenefitsInfo(dynamicInfo);

    const benefitsToShow = memberStatus.benefitsToShow;
    const memberBenefits = memberLevelBenefits?.find((item: any) => item.key === benefitsToShow);

    if (memberBenefits) {
      const enhancedMemberBenefits = {
        ...memberBenefits,
        dynamicTitle: dynamicContent?.title || '',
        dynamicSubtitle: dynamicContent?.subtitle || '',
        isLocked: memberStatus.isLocked,
        memberStatus: memberStatus.status,
      };

      setModalMemberBenefits(enhancedMemberBenefits);
    }
  } else {
    const memberBenefits = memberLevelBenefits?.find((item: any) => item.key === code);
    if (memberBenefits) {
      setModalMemberBenefits(memberBenefits);
    }
  }

  // Set tier position based on member status
  if (memberLevels && memberLevels.length > 0 && !hasInitialized.current) {
    const reorderedList = memberLevels;
    let targetIndex = reorderedList?.findIndex((item: any) => item?.id === mappedCode);
    let targetMember = mappedCode;

    if (memberLevelBenefits && nextLevelMapping) {
      const memberStatus = determineMemberStatus(
        code,
        currentNights,
        memberLevelBenefits,
        nextLevelMapping,
        currentSpend,
        lifetimeStatus,
        dataSource
      );

      // Handle tier selection based on member status
      if (memberStatus.status === MEMBER_STATUS.NEAR_ACHIEVING && memberStatus.targetTier) {
        // Near Achieving: Show NEXT tier (locked)
        const nextTierIndex = reorderedList?.findIndex((item: any) => item?.id === memberStatus.targetTier);
        if (nextTierIndex !== -1) {
          targetIndex = nextTierIndex;
          targetMember = memberStatus.targetTier;
        }
      } else if (memberStatus.status === MEMBER_STATUS.NEAR_RENEWING) {
        // Near Renewing: EXPLICITLY show CURRENT tier (not next tier)
        // Ensure we use the user's actual current tier, not the target tier
        targetIndex = reorderedList?.findIndex((item: any) => item?.id === mappedCode);
        targetMember = mappedCode;
      }
      // For NOT_NEAR, the default targetIndex and targetMember (set above) will be used
    }

    if (targetIndex !== -1) {
      setActiveIndex(targetIndex);
      setCurrentMember(targetMember);
      hasInitialized.current = true;
    }
  }

  setMemberProperties(getMemberLevelPropertiesByCode(member?.id || '', memberStatusList));
};

/**
 * Handles viewport switching logic for tier selection between desktop and mobile views
 *
 * @param isMobileViewPort - Whether the current viewport is mobile (true) or desktop (false)
 * @param hasReversedForMobile - React ref tracking if the array has been reversed for mobile view
 * @param memberLevels - Current array of member tier levels
 * @param activeIndex - Current active tier index in the array
 * @param originalDesktopOrder - React ref containing the original desktop order for restoration
 * @returns Object containing new member levels and active index, or null if no viewport change needed
 */
export const handleViewportTierSwitching = (
  isMobileViewPort: boolean,
  hasReversedForMobile: React.MutableRefObject<boolean>,
  memberLevels: any[],
  activeIndex: number,
  originalDesktopOrder: React.MutableRefObject<any[]>
): { newLevels: any[]; newActiveIndex: number } | null => {
  // Handle mobile viewport changes
  if (isMobileViewPort && !hasReversedForMobile.current) {
    // Create a new reversed array for mobile (Member → Ambassador, top to bottom)
    const reversedLevels = [...memberLevels].reverse();
    // Calculate new activeIndex based on the reverse
    const newActiveIndex = memberLevels.length - 1 - activeIndex;
    hasReversedForMobile.current = true;
    return { newLevels: reversedLevels, newActiveIndex };
  } else if (!isMobileViewPort && hasReversedForMobile.current) {
    // Reset the flag when switching back to desktop
    hasReversedForMobile.current = false;
    // Restore desktop order using the original desktop order
    if (originalDesktopOrder.current && originalDesktopOrder.current.length > 0) {
      const desktopOrderedLevels = [...originalDesktopOrder.current];
      // Find the correct position by using the current tier ID from the mobile array
      // The mobile array is reversed, so we need to get the tier from the mobile array
      const currentTierId = memberLevels[activeIndex]?.id;

      if (currentTierId) {
        const newActiveIndex = desktopOrderedLevels.findIndex(level => level.id === currentTierId);
        if (newActiveIndex !== -1) {
          return { newLevels: desktopOrderedLevels, newActiveIndex };
        }
      }

      // Fallback: use mathematical calculation if tier ID lookup fails
      // Use the original activeIndex as fallback (this matches the test expectation)
      const safeIndex = Math.min(activeIndex, desktopOrderedLevels.length - 1);

      return { newLevels: desktopOrderedLevels, newActiveIndex: safeIndex };
    }
  }
  return null;
};

export const shouldLockBenefits = (
  selectedTierId: string,
  dynamicBenefitsInfo: any,
  shouldUseDynamicContent: boolean
): boolean => {
  // For Near Achieving users, only lock benefits for the target tier and above
  // Current tier and all tiers below should be unlocked (benefits build, never drop)
  if (shouldUseDynamicContent && dynamicBenefitsInfo?.isLocked && dynamicBenefitsInfo?.targetTier) {
    const targetIndex = CORE_LEVEL_CODES.indexOf(dynamicBenefitsInfo.targetTier);
    const tierIndex = CORE_LEVEL_CODES.indexOf(selectedTierId);
    return tierIndex >= targetIndex; // Same or higher index = same or higher tier
  }

  return false;
};

/**
 * Determines if a tier is elevated (higher than user's actual tier)
 * @param currentTierId - The tier being viewed
 * @param userActualTierId - The user's actual tier
 * @returns true if current tier is higher than user's actual tier
 */
export const isTierElevated = (currentTierId: string | undefined, userActualTierId: string | undefined): boolean => {
  if (!currentTierId || !userActualTierId) {
    return false;
  }

  const currentTierIndex = CORE_LEVEL_CODES.indexOf(currentTierId);
  const userActualTierIndex = CORE_LEVEL_CODES.indexOf(userActualTierId);

  // Return false if either tier code is invalid (not found in CORE_LEVEL_CODES)
  if (currentTierIndex === -1 || userActualTierIndex === -1) {
    return false;
  }

  // Elevated if viewing a tier higher than user's actual tier
  return currentTierIndex > userActualTierIndex;
};

/**
 * Shared hook for phoenixAccountGetMemberStatusDetails query
 * Centralizes UXL data fetching logic used across multiple components
 *
 * REUSABILITY: This hook can be used in any component that needs:
 * - Member status details from UXL API
 * - Conditional data fetching based on authentication
 * - Centralized query configuration and error handling
 *
 * USAGE: Import and use in components that need member status data:
 * - MembershipBenefits
 * - MemberSnapshotRibbon
 * - MemberStatus
 *
 * Can be reused across different components
 */
export const useMemberStatusDetails = (
  customerId: string | undefined,
  startYear: number,
  pageContext: any,
  isLocalDev: boolean,
  onCompleted?: (data: any) => void,
  onError?: (error: any) => void
) => {
  // Memoize callbacks to prevent infinite re-renders
  const memoizedOnCompleted = useCallback(
    (data: any) => {
      onCompleted?.(data);
    },
    [onCompleted]
  );

  const memoizedOnError = useCallback(
    (error: any) => {
      onError?.(error);
    },
    [onError]
  );

  return useLazyQuery(phoenixAccountGetMemberStatusDetails, {
    variables: {
      customerId,
      startYear,
    },
    context: generateApolloClientHeaders(isLocalDev, pageContext),
    fetchPolicy: 'no-cache',
    onCompleted: memoizedOnCompleted,
    onError: memoizedOnError,
  });
};

/** Shared dynamic benefits info type */
export interface DynamicBenefitsInfoParam {
  memberStatus?: string;
  benefitsToShow?: string;
  dynamicTitle?: string;
  dynamicSubtitle?: string;
}

/** Shared tier comparison type */
export interface TierComparisonParam {
  selectedTierIndex: number;
  isViewingHigherTier: boolean;
  isViewingLowerTier: boolean;
  isViewingOwnTier: boolean;
}

/** Interface for benefits title/subtitle calculation params */
export interface BenefitsTitleSubtitleParams {
  selectedTierId: string;
  currentMember: string | undefined;
  memberLevelBenefits: IntegratedMemberLevelBenefit[];
  dynamicBenefitsInfo?: DynamicBenefitsInfoParam;
  tierComparison: TierComparisonParam;
}

/** Interface for benefits title/subtitle result */
export interface BenefitsTitleSubtitleResult {
  displayTitle: string;
  displaySubtitle: string;
}

/**
 * WEB-170249: Calculates the display title and subtitle for the Benefits Module/Modal
 * Logic based on tier relationship (viewing higher/lower/own tier) and member status
 *
 * @param params - Title/subtitle calculation parameters
 * @returns Object with displayTitle and displaySubtitle
 */
export const getBenefitsTitleAndSubtitle = (params: BenefitsTitleSubtitleParams): BenefitsTitleSubtitleResult => {
  const { selectedTierId, currentMember, memberLevelBenefits, dynamicBenefitsInfo, tierComparison } = params;
  const { selectedTierIndex, isViewingHigherTier, isViewingOwnTier } = tierComparison;

  const currentTierBenefits = memberLevelBenefits?.find(item => item.key === selectedTierId);
  const userCurrentTierBenefits = memberLevelBenefits?.find(item => item.key === currentMember);

  const shouldUseDynamicContent =
    dynamicBenefitsInfo?.dynamicTitle &&
    (dynamicBenefitsInfo?.memberStatus === MEMBER_STATUS.NEAR_ACHIEVING ||
      dynamicBenefitsInfo?.memberStatus === MEMBER_STATUS.NEAR_RENEWING);

  // Title logic
  let displayTitle: string;
  if (shouldUseDynamicContent && isViewingOwnTier) {
    displayTitle = dynamicBenefitsInfo?.dynamicTitle || '';
  } else if (shouldUseDynamicContent && dynamicBenefitsInfo?.benefitsToShow === selectedTierId) {
    displayTitle = dynamicBenefitsInfo?.dynamicTitle || '';
  } else {
    displayTitle = currentTierBenefits?.notNearTitle || '';
  }

  // Subtitle logic
  let displaySubtitle: string;
  if (shouldUseDynamicContent && (isViewingOwnTier || dynamicBenefitsInfo?.benefitsToShow === selectedTierId)) {
    displaySubtitle = dynamicBenefitsInfo?.dynamicSubtitle || '';
  } else if (isViewingHigherTier) {
    const previousTierKey = selectedTierIndex > 0 ? CORE_LEVEL_CODES[selectedTierIndex - 1] : null;
    const previousTierBenefits = previousTierKey
      ? memberLevelBenefits?.find(item => item.key === previousTierKey)
      : null;

    // Ambassador special case
    if (selectedTierId === AmbassadorMemberCode) {
      const isNearAchievingToAmbassador =
        dynamicBenefitsInfo?.memberStatus === MEMBER_STATUS.NEAR_ACHIEVING &&
        dynamicBenefitsInfo?.benefitsToShow === AmbassadorMemberCode;
      if (isNearAchievingToAmbassador) {
        // User is near achieving Ambassador - use Titanium's nearAchievingSubTitle
        displaySubtitle = previousTierBenefits?.nearAchievingSubTitle || '';
      } else {
        // User is NOT near achieving Ambassador and viewing Ambassador from a lower tier
        // Use Ambassador's own notNearSubTitle which says "Enjoy all the previous perks..."
        // Do NOT use Titanium's notNearSubTitle as it says "Unlocked at 75 nights" which is incorrect for Ambassador
        displaySubtitle = currentTierBenefits?.notNearSubTitle || '';
      }
    } else {
      // For non-Ambassador higher tiers, use the PREVIOUS tier's nearAchievingSubTitle
      // which says "Will unlock at X nights..."
      const previousTierNearAchievingSubTitle = previousTierBenefits?.nearAchievingSubTitle;

      // Edge case: Don't use tier-before-Ambassador's subtitle when viewing tiers below Ambassador
      // as it references Ambassador which doesn't make sense in that context
      const ambassadorIndex = CORE_LEVEL_CODES.indexOf(AmbassadorMemberCode);
      const tierBeforeAmbassadorKey = ambassadorIndex > 0 ? CORE_LEVEL_CODES[ambassadorIndex - 1] : null;
      const isPreviousTierBeforeAmbassador = previousTierKey === tierBeforeAmbassadorKey;
      const isViewingBelowAmbassador = selectedTierId !== AmbassadorMemberCode;

      if (previousTierNearAchievingSubTitle && !(isPreviousTierBeforeAmbassador && isViewingBelowAmbassador)) {
        displaySubtitle = previousTierNearAchievingSubTitle;
      } else {
        displaySubtitle = currentTierBenefits?.notNearSubTitle || '';
      }
    }
  } else {
    displaySubtitle = currentTierBenefits?.notNearSubTitle || '';
  }

  return { displayTitle, displaySubtitle };
};

/**
 * WEB-170249: Gets the display title based on tier relationship and member status
 * Reusable for both module and modal title logic
 */
export const getBenefitsDisplayTitle = (params: {
  selectedTierId: string;
  viewedTierTitle: string | undefined;
  fallbackTitle: string;
  dynamicBenefitsInfo?: DynamicBenefitsInfoParam;
  tierComparison: TierComparisonParam;
}): string => {
  const { selectedTierId, viewedTierTitle, fallbackTitle, dynamicBenefitsInfo, tierComparison } = params;
  const { isViewingOwnTier } = tierComparison;

  const shouldUseDynamicContent =
    dynamicBenefitsInfo?.dynamicTitle &&
    (dynamicBenefitsInfo?.memberStatus === MEMBER_STATUS.NEAR_ACHIEVING ||
      dynamicBenefitsInfo?.memberStatus === MEMBER_STATUS.NEAR_RENEWING);

  if (shouldUseDynamicContent && isViewingOwnTier) {
    return dynamicBenefitsInfo?.dynamicTitle || '';
  }
  if (shouldUseDynamicContent && dynamicBenefitsInfo?.benefitsToShow === selectedTierId) {
    return dynamicBenefitsInfo?.dynamicTitle || '';
  }
  return viewedTierTitle || fallbackTitle;
};
